import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
    Plus,
    Trash2,
    LogOut,
    Eye,
    Upload,
    FileText,
    ImageIcon,
    Lock,
    ArrowLeft,
    X,
} from 'lucide-react';
import {
    listSamples,
    createSample,
    deleteSample,
    adminLogin,
    adminVerify,
    adminLogout,
    getAdminToken,
    setAdminToken,
    CONTENT_TYPES,
    LANGUAGES,
} from '@/lib/api';
import { cn, hasBengali, readFileAsDataURL } from '@/lib/utils';
import { Link } from 'react-router-dom';

function PasswordGate({ onSuccess }) {
    const [pw, setPw] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!pw) {
            setError('Please enter the admin password.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const { token } = await adminLogin(pw);
            setAdminToken(token);
            onSuccess();
        } catch (err) {
            const detail =
                err?.response?.status === 401
                    ? 'That password didn’t match. Try again.'
                    : err?.response?.data?.detail ||
                      'Could not sign in. Please try again.';
            setError(
                typeof detail === 'string'
                    ? detail
                    : 'That password didn’t match. Try again.'
            );
        } finally {
            setSubmitting(false);
        }
    }
    return (
        <Dialog open>
            <DialogContent
                className="max-w-md"
                data-testid="admin-password-dialog"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
            >
                <DialogHeader className="text-left">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-2">
                        <Lock className="h-4 w-4" />
                    </div>
                    <DialogTitle>Admin access</DialogTitle>
                    <DialogDescription>
                        Enter the admin password to manage the portfolio.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Input
                        type="password"
                        placeholder="Password"
                        value={pw}
                        autoFocus
                        disabled={submitting}
                        onChange={(e) => setPw(e.target.value)}
                        data-testid="admin-password-input"
                    />
                    {error && (
                        <p
                            className="text-xs text-destructive"
                            data-testid="admin-password-error-text"
                        >
                            {error}
                        </p>
                    )}
                    <DialogFooter className="flex-col sm:flex-col gap-2">
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={submitting}
                            data-testid="admin-password-submit-button"
                        >
                            {submitting ? 'Signing in…' : 'Enter dashboard'}
                        </Button>
                        <Button
                            asChild
                            type="button"
                            variant="ghost"
                            className="w-full"
                        >
                            <Link to="/">
                                <ArrowLeft className="mr-1 h-4 w-4" />
                                Back to portfolio
                            </Link>
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function AddSampleDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState(empty());
    const [saving, setSaving] = useState(false);

    function empty() {
        return {
            title: '',
            contentType: '',
            language: '',
            excerpt: '',
            content: '',
            externalUrl: '',
            attachmentName: '',
            attachmentMime: '',
            attachmentBase64: '',
        };
    }

    useEffect(() => {
        if (!open) setForm(empty());
    }, [open]);

    async function handleFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 4.5 * 1024 * 1024) {
            toast.error('File is too large. Keep it under 4MB.');
            e.target.value = '';
            return;
        }
        try {
            const dataUrl = await readFileAsDataURL(file);
            setForm((f) => ({
                ...f,
                attachmentName: file.name,
                attachmentMime: file.type,
                attachmentBase64: dataUrl,
            }));
        } catch {
            toast.error('Could not read that file.');
        }
    }

    function clearAttachment() {
        setForm((f) => ({
            ...f,
            attachmentName: '',
            attachmentMime: '',
            attachmentBase64: '',
        }));
    }

    async function submit(e) {
        e.preventDefault();
        if (!form.title || !form.contentType || !form.language || !form.excerpt || !form.content) {
            toast.error('Title, type, language, excerpt and content are required.');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                ...form,
                externalUrl: form.externalUrl || null,
                attachmentName: form.attachmentName || null,
                attachmentMime: form.attachmentMime || null,
                attachmentBase64: form.attachmentBase64 || null,
            };
            const created = await createSample(payload);
            toast.success('Sample added — live on the portfolio.');
            onCreated(created);
            onOpenChange(false);
        } catch (err) {
            const detail =
                err?.response?.data?.detail || 'Could not save sample.';
            toast.error(
                typeof detail === 'string' ? detail : 'Could not save sample.'
            );
        } finally {
            setSaving(false);
        }
    }

    const isImage = (form.attachmentMime || '').startsWith('image/');
    const isPdf = form.attachmentMime === 'application/pdf';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl max-h-[92vh] overflow-y-auto"
                data-testid="admin-add-sample-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Add a new work sample</DialogTitle>
                    <DialogDescription>
                        Once saved, this appears live in the public portfolio
                        immediately.
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={submit}
                    className="grid gap-6 md:grid-cols-2"
                >
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Title</Label>
                            <Input
                                value={form.title}
                                onChange={(e) =>
                                    setForm({ ...form, title: e.target.value })
                                }
                                placeholder="e.g. Money Moves — Issue #51"
                                data-testid="admin-sample-title-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Content type</Label>
                                <Select
                                    value={form.contentType}
                                    onValueChange={(v) =>
                                        setForm({ ...form, contentType: v })
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="admin-sample-type-select"
                                    >
                                        <SelectValue placeholder="Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CONTENT_TYPES.map((t) => (
                                            <SelectItem key={t} value={t}>
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Language</Label>
                                <Select
                                    value={form.language}
                                    onValueChange={(v) =>
                                        setForm({ ...form, language: v })
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="admin-sample-language-select"
                                    >
                                        <SelectValue placeholder="Language" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LANGUAGES.map((l) => (
                                            <SelectItem key={l} value={l}>
                                                {l}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Excerpt</Label>
                            <Textarea
                                rows={3}
                                value={form.excerpt}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        excerpt: e.target.value,
                                    })
                                }
                                placeholder="Short teaser shown on the portfolio card."
                                data-testid="admin-sample-excerpt-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Full content</Label>
                            <Textarea
                                rows={10}
                                value={form.content}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        content: e.target.value,
                                    })
                                }
                                placeholder="Full sample content. Shown inside the read-modal."
                                data-testid="admin-sample-content-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>External link (optional)</Label>
                            <Input
                                type="url"
                                value={form.externalUrl}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        externalUrl: e.target.value,
                                    })
                                }
                                placeholder="https://… the live published piece"
                                data-testid="admin-sample-link-input"
                            />
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div className="rounded-xl border border-border bg-muted/40 p-4">
                            <p className="kicker mb-3">Attachment preview</p>
                            <Label className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-background px-4 py-6 cursor-pointer hover:border-primary/50 transition-colors">
                                <Upload className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                    {form.attachmentName ||
                                        'Click to upload PDF or image'}
                                </span>
                                <input
                                    type="file"
                                    accept="image/*,application/pdf"
                                    className="hidden"
                                    onChange={handleFile}
                                    data-testid="admin-sample-upload-input"
                                />
                            </Label>
                            {form.attachmentBase64 && (
                                <div className="mt-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs flex items-center gap-1.5 text-muted-foreground">
                                            {isImage ? (
                                                <ImageIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <FileText className="h-3.5 w-3.5" />
                                            )}
                                            {form.attachmentName}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={clearAttachment}
                                            className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
                                        >
                                            <X className="h-3 w-3" />
                                            Remove
                                        </button>
                                    </div>
                                    {isImage ? (
                                        <img
                                            src={form.attachmentBase64}
                                            alt="preview"
                                            className="w-full max-h-[260px] object-contain rounded-md bg-card border border-border"
                                        />
                                    ) : isPdf ? (
                                        <a
                                            href={form.attachmentBase64}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-sm text-primary hover:underline"
                                        >
                                            Open PDF in new tab
                                        </a>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            File ready to upload.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl border border-border bg-card p-4">
                            <p className="kicker mb-2">Card preview</p>
                            <p className="text-xs text-muted-foreground mb-2">
                                {form.contentType || 'Type'} ·{' '}
                                {form.language || 'Language'}
                            </p>
                            <p
                                className={cn(
                                    'font-heading text-base leading-snug',
                                    hasBengali(form.title) && 'font-bn'
                                )}
                            >
                                {form.title || 'Sample title appears here'}
                            </p>
                            <p className="mt-2 text-xs text-muted-foreground line-clamp-3">
                                {form.excerpt ||
                                    'Excerpt preview. This is what visitors will see on the card.'}
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="md:col-span-2 flex-col sm:flex-row gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="admin-save-sample-button"
                        >
                            {saving ? 'Saving…' : 'Save sample'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Dashboard({ onLogout }) {
    const [samples, setSamples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAdd, setOpenAdd] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    async function reload() {
        setLoading(true);
        try {
            const data = await listSamples();
            setSamples(data || []);
            if (data && data.length && !selectedId) setSelectedId(data[0].id);
        } catch {
            toast.error('Could not load samples.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const grouped = useMemo(() => {
        const counts = {};
        for (const s of samples) {
            counts[s.contentType] = (counts[s.contentType] || 0) + 1;
        }
        return counts;
    }, [samples]);

    const selected = useMemo(
        () => samples.find((s) => s.id === selectedId) || null,
        [samples, selectedId]
    );

    async function confirmDelete() {
        if (!confirmId) return;
        try {
            await deleteSample(confirmId);
            toast.success('Sample deleted.');
            setSamples((prev) => prev.filter((s) => s.id !== confirmId));
            if (selectedId === confirmId) setSelectedId(null);
            setConfirmId(null);
        } catch {
            toast.error('Could not delete that sample.');
        }
    }

    function onCreated(created) {
        setSamples((prev) => [created, ...prev]);
        setSelectedId(created.id);
    }

    return (
        <div
            className="min-h-screen bg-background text-foreground"
            data-testid="admin-dashboard"
        >
            <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
                <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/"
                            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            Portfolio
                        </Link>
                        <Separator
                            orientation="vertical"
                            className="h-4"
                        />
                        <p className="font-heading text-lg sm:text-xl tracking-tight">
                            Pallabi · CMS
                        </p>
                        <Badge
                            variant="secondary"
                            className="hidden sm:inline-flex rounded-full text-[10px] uppercase tracking-[0.14em]"
                        >
                            Admin
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            onClick={() => setOpenAdd(true)}
                            className="rounded-md"
                            data-testid="admin-add-sample-button"
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Add sample</span>
                            <span className="sm:hidden">Add</span>
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onLogout}
                            data-testid="admin-logout-button"
                        >
                            <LogOut className="h-4 w-4 mr-1" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>
            <div className="mx-auto max-w-[1400px] grid grid-cols-1 gap-4 px-3 sm:px-4 lg:px-6 py-4 md:grid-cols-[380px_1fr]">
                {/* Sample manager */}
                <section
                    className="rounded-[14px] border border-border bg-card overflow-hidden"
                    data-testid="admin-sample-manager-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <div>
                            <p className="kicker">Sample manager</p>
                            <p className="text-sm font-medium mt-0.5">
                                {samples.length} samples
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-1 justify-end max-w-[180px]">
                            {Object.entries(grouped).slice(0, 4).map(([t, c]) => (
                                <Badge
                                    key={t}
                                    variant="outline"
                                    className="text-[10px] font-normal"
                                >
                                    {t} {c}
                                </Badge>
                            ))}
                        </div>
                    </div>
                    <ScrollArea className="h-[calc(100vh-220px)]">
                        <ul
                            className="divide-y divide-border"
                            data-testid="admin-sample-list"
                        >
                            {loading && (
                                <li className="px-4 py-6 text-sm text-muted-foreground">
                                    Loading…
                                </li>
                            )}
                            {!loading && samples.length === 0 && (
                                <li className="px-4 py-10 text-sm text-muted-foreground text-center">
                                    No samples yet. Click “Add sample” to
                                    create the first one.
                                </li>
                            )}
                            {samples.map((s) => (
                                <li
                                    key={s.id}
                                    className={cn(
                                        'flex items-center justify-between gap-3 px-4 py-3 group hover:bg-accent/60 transition-colors cursor-pointer',
                                        selectedId === s.id && 'bg-accent'
                                    )}
                                    onClick={() => setSelectedId(s.id)}
                                    data-testid="admin-sample-row"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p
                                            className={cn(
                                                'text-sm font-medium truncate',
                                                hasBengali(s.title) &&
                                                    'font-bn'
                                            )}
                                        >
                                            {s.title}
                                        </p>
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <Badge
                                                variant="secondary"
                                                className="text-[10px] font-normal"
                                            >
                                                {s.contentType}
                                            </Badge>
                                            <span className="text-[10px] text-muted-foreground">
                                                {s.language}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmId(s.id);
                                        }}
                                        aria-label="Delete sample"
                                        data-testid="admin-delete-sample-button"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </ScrollArea>
                </section>

                {/* Live preview */}
                <section
                    className="rounded-[14px] border border-border bg-card overflow-hidden"
                    data-testid="admin-live-preview-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <div>
                            <p className="kicker flex items-center gap-1.5">
                                <Eye className="h-3 w-3" />
                                Live preview
                            </p>
                            <p className="text-sm font-medium mt-0.5">
                                {selected
                                    ? 'Selected sample'
                                    : 'Portfolio overview'}
                            </p>
                        </div>
                        <Button asChild size="sm" variant="outline">
                            <Link to="/" target="_blank" rel="noreferrer">
                                Open portfolio
                            </Link>
                        </Button>
                    </div>
                    <div className="p-4 max-h-[calc(100vh-220px)] overflow-y-auto">
                        {selected ? (
                            <div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge
                                        variant="secondary"
                                        className="rounded-full"
                                    >
                                        {selected.contentType}
                                    </Badge>
                                    <Badge
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        {selected.language}
                                    </Badge>
                                    {selected.externalUrl && (
                                        <a
                                            href={selected.externalUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-primary hover:underline"
                                        >
                                            View external ↗
                                        </a>
                                    )}
                                </div>
                                <h3
                                    className={cn(
                                        'mt-3 font-heading text-2xl leading-tight',
                                        hasBengali(selected.title) &&
                                            'font-bn'
                                    )}
                                >
                                    {selected.title}
                                </h3>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    {selected.excerpt}
                                </p>
                                <Separator className="my-4" />
                                <article
                                    className={cn(
                                        'reading-prose max-w-prose',
                                        hasBengali(selected.content) &&
                                            'font-bn reading-prose-bn'
                                    )}
                                >
                                    {selected.content}
                                </article>
                                {selected.htmlContent && (
                                    <div className="mt-4 rounded-md border border-border bg-[#f4f4f4] p-3">
                                        <p className="kicker mb-2">
                                            HTML preview
                                        </p>
                                        <iframe
                                            srcDoc={selected.htmlContent}
                                            title={selected.title}
                                            className="w-full bg-white rounded border border-border"
                                            style={{ height: '60vh' }}
                                            sandbox="allow-same-origin"
                                        />
                                    </div>
                                )}
                                {(selected.attachmentBase64 ||
                                    selected.attachmentUrl) && (
                                    <div className="mt-4 rounded-md border border-border bg-muted/40 p-3">
                                        <p className="kicker mb-2">
                                            Attachment
                                        </p>
                                        {(selected.attachmentMime || '').startsWith(
                                            'image/'
                                        ) ? (
                                            <img
                                                src={
                                                    selected.attachmentUrl ||
                                                    selected.attachmentBase64
                                                }
                                                alt="attachment"
                                                className="max-h-[260px] object-contain rounded"
                                            />
                                        ) : (
                                            <a
                                                href={
                                                    selected.attachmentUrl ||
                                                    selected.attachmentBase64
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-sm text-primary hover:underline"
                                            >
                                                {selected.attachmentName ||
                                                    'Open file'}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Read-only mini preview of the portfolio
                                    state. Select a sample on the left to
                                    inspect it.
                                </p>
                                <ul className="divide-y divide-border rounded-md border border-border">
                                    {samples.map((s) => (
                                        <li
                                            key={s.id}
                                            className="px-3 py-2 text-sm flex items-center justify-between gap-3"
                                        >
                                            <span
                                                className={cn(
                                                    'truncate',
                                                    hasBengali(s.title) &&
                                                        'font-bn'
                                                )}
                                            >
                                                {s.title}
                                            </span>
                                            <Badge
                                                variant="outline"
                                                className="text-[10px] font-normal shrink-0"
                                            >
                                                {s.contentType}
                                            </Badge>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <AddSampleDialog
                open={openAdd}
                onOpenChange={setOpenAdd}
                onCreated={onCreated}
            />
            <AlertDialog
                open={!!confirmId}
                onOpenChange={(o) => !o && setConfirmId(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this sample?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the sample from the public
                            portfolio immediately. This action can’t be
                            undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="admin-delete-cancel-button"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="admin-delete-confirm-button"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function AdminDashboard() {
    const [authState, setAuthState] = useState(() =>
        getAdminToken() ? 'checking' : 'unauth'
    );

    // On mount, if we have a stored token, verify it against the backend.
    // Handles the case where the server was restarted (in-memory tokens cleared).
    useEffect(() => {
        if (authState !== 'checking') return;
        let cancelled = false;
        adminVerify()
            .then(() => {
                if (!cancelled) setAuthState('authed');
            })
            .catch(() => {
                if (!cancelled) {
                    setAdminToken(null);
                    setAuthState('unauth');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [authState]);

    async function logout() {
        await adminLogout();
        setAuthState('unauth');
        toast.info('Logged out.');
    }

    if (authState === 'checking') {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
                Verifying session…
            </div>
        );
    }
    if (authState === 'unauth') {
        return <PasswordGate onSuccess={() => setAuthState('authed')} />;
    }
    return <Dashboard onLogout={logout} />;
}
